import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { QRCodeSVG } from "qrcode.react";
import { useForm } from "react-hook-form";
import { Alert, Button } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import { adminTotpConfirmationSchema, normalizePastedTotpCode } from "../schema";
import type { AdminTotpConfirmationFormValues } from "../schema";
import type { Schemas } from "../../workspace/api.generated";
import type { LoginFeedback } from "./LoginView";

export interface TotpSetupViewProps {
  readonly enrollment?: Schemas["AdminTotpEnrollmentResponse"] | null;
  readonly error?: LoginFeedback | null;
  readonly issuing?: boolean;
  readonly recoveryCodes?: readonly string[] | null;
  readonly onBeginEnrollment: () => Promise<void>;
  readonly onClearError?: () => void;
  readonly onConfirm: (input: Schemas["ConfirmAdminTotpRequest"]) => Promise<boolean>;
  readonly onCopyEnrollmentSecret: () => Promise<void>;
  readonly onCopyRecoveryCodes: () => Promise<void>;
  readonly onRecoveryCodesStored: () => void;
}

export function TotpSetupView({
  enrollment,
  error,
  issuing = false,
  recoveryCodes,
  onBeginEnrollment,
  onClearError,
  onConfirm,
  onCopyEnrollmentSecret,
  onCopyRecoveryCodes,
  onRecoveryCodesStored,
}: TotpSetupViewProps) {
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const form = useForm<AdminTotpConfirmationFormValues>({
    resolver: zodResolver(adminTotpConfirmationSchema),
    defaultValues: { code: "" },
    shouldFocusError: true,
  });

  if (recoveryCodes?.length) {
    return (
      <>
        <h1>복구코드를 보관해 주세요</h1>
        <p>이 화면을 떠난 뒤에는 같은 복구코드를 다시 확인할 수 없습니다.</p>
        <Alert persistent variant="warning" title="각 코드는 한 번만 사용할 수 있습니다">
          관리자 계정에 접근할 수 있는 안전한 비밀번호 보관함에 저장해 주세요.
        </Alert>
        <ol aria-label="TOTP 복구코드" className="admin-code-list">
          {recoveryCodes.map((code) => <li key={code}><code>{code}</code></li>)}
        </ol>
        {copyFeedback ? <p aria-live="polite" className="admin-auth__help" role="status">{copyFeedback}</p> : null}
        <div className="admin-auth__actions">
          <Button
            size="large"
            variant="secondary"
            onClick={async () => {
              setCopyFeedback(null);
              try {
                await onCopyRecoveryCodes();
                setCopyFeedback("복구코드를 클립보드에 복사했습니다.");
              } catch {
                setCopyFeedback("복사하지 못했습니다. 코드를 직접 선택해 저장해 주세요.");
              }
            }}
          >
            복구코드 복사
          </Button>
          <Button size="large" onClick={onRecoveryCodesStored}>보관 완료하고 업무 시작</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>TOTP를 등록해 주세요</h1>
      <p>휴대전화의 TOTP 인증 앱을 연결한 뒤 현재 숫자 6자리를 확인합니다.</p>
      {error ? (
        <Alert persistent variant="danger" title={error.title}>
          {error.message}
        </Alert>
      ) : null}
      {!enrollment ? (
        <>
          <Alert persistent variant="information" title="TOTP 인증 앱이 필요합니다">
            Google Authenticator, Microsoft Authenticator, 1Password처럼 TOTP를 지원하는 앱을 준비해 주세요. 앱이 없다면 먼저 설치한 뒤 등록을 시작하세요.
          </Alert>
          <Button
            className="admin-auth__submit"
            isLoading={issuing}
            loadingLabel="등록 정보 발급 중"
            size="large"
            onClick={onBeginEnrollment}
          >
            등록 정보 발급
          </Button>
        </>
      ) : (
        <>
          <div className="admin-totp-enrollment">
            <figure className="admin-totp-qr">
              <QRCodeSVG
                aria-label="CarePass TOTP 등록 QR 코드"
                className="admin-totp-qr__image"
                level="M"
                marginSize={4}
                title="CarePass TOTP 등록 QR 코드"
                value={enrollment.provisioningUri}
              />
              <figcaption>인증 앱에서 QR 코드를 스캔해 CarePass 계정을 추가하세요.</figcaption>
            </figure>
          </div>
          <form
            noValidate
            onSubmit={form.handleSubmit(async (values) => {
              onClearError?.();
              const confirmed = await onConfirm(values);
              if (confirmed) form.reset();
            })}
          >
            <HookFormField
              autoComplete="one-time-code"
              description="인증 앱에 표시된 현재 숫자 6자리를 입력합니다."
              formControl={form.control}
              inputMode="numeric"
              label="현재 TOTP 코드"
              maxLength={6}
              name="code"
              onPaste={(event) => {
                const normalized = normalizePastedTotpCode(event.clipboardData.getData("text"));
                if (!normalized) return;
                event.preventDefault();
                form.setValue("code", normalized, { shouldDirty: true, shouldValidate: true });
              }}
              pattern="[0-9]*"
              required
            />
            <Button
              className="admin-auth__submit"
              isLoading={form.formState.isSubmitting}
              loadingLabel="TOTP 확인 중"
              size="large"
              type="submit"
            >
              코드 확인
            </Button>
          </form>
          <details className="admin-totp-enrollment__manual">
            <summary>QR 코드를 스캔할 수 없나요?</summary>
            <p>인증 앱의 설정 키 직접 입력 메뉴에서 아래 값을 사용하세요.</p>
            <dl className="admin-secret">
              <div><dt>설정 키</dt><dd><code>{enrollment.secret}</code></dd></div>
            </dl>
            <Button
              size="small"
              type="button"
              variant="secondary"
              onClick={async () => {
                setCopyFeedback(null);
                try {
                  await onCopyEnrollmentSecret();
                  setCopyFeedback("설정 키를 클립보드에 복사했습니다.");
                } catch {
                  setCopyFeedback("복사하지 못했습니다. 설정 키를 직접 선택해 주세요.");
                }
              }}
            >
              설정 키 복사
            </Button>
            {copyFeedback ? <p aria-live="polite" className="admin-auth__help" role="status">{copyFeedback}</p> : null}
          </details>
        </>
      )}
    </>
  );
}
